/**
 * 寝る準備の 1 ステップ（「今日」のタイムラインに時間帯と並べて出す）
 *
 * ベルで項目ごとに通知のオン・オフを切り替える。
 */

import { Bell, BellOff, Moon } from 'lucide-react'

import type { SleepPrepStep, SleepSettings } from '@/api/types/sleep'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useSleepStore } from '@/store/useSleepStore'

import { formatJstTime, formatMinutes } from '../format'

export const PrepStepItem = ({
  step,
  settings,
  past,
}: {
  step: SleepPrepStep
  settings: SleepSettings
  past: boolean
}) => {
  const saveSettings = useSleepStore((s) => s.saveSettings)
  const notifiable = step.notify_lead_min !== null
  const notifyOn = notifiable && !settings.prep_notify_off.includes(step.key)

  const toggleNotify = () => {
    const next = notifyOn
      ? [...settings.prep_notify_off, step.key]
      : settings.prep_notify_off.filter((k) => k !== step.key)
    void saveSettings({ ...settings, prep_notify_off: next })
  }

  return (
    <li className="relative flex items-start gap-2 pb-4 pl-4">
      <span className="absolute top-1.5 -left-[5px] size-2.5 rounded-full border-2 border-background bg-indigo-400" />
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg bg-indigo-50/70 px-2.5 py-1.5 dark:bg-indigo-950/30',
          past && 'opacity-50',
        )}
      >
        <p className="flex items-center gap-1.5 text-sm">
          <Moon className="size-3.5 shrink-0 text-indigo-500" />
          <span className="font-semibold tabular-nums">{formatJstTime(step.at)}</span>
          {step.title}
        </p>
        <p className="text-xs text-muted-foreground">{step.detail}</p>
        {notifyOn && !!step.notify_lead_min && (
          <p className="text-xs text-indigo-600 dark:text-indigo-300">{formatMinutes(step.notify_lead_min)}前に通知</p>
        )}
      </div>
      {notifiable && (
        <Button
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          aria-label={notifyOn ? '通知をオフにする' : '通知をオンにする'}
          onClick={toggleNotify}
        >
          {notifyOn ? <Bell className="text-indigo-600" /> : <BellOff className="text-muted-foreground" />}
        </Button>
      )}
    </li>
  )
}
